<script setup>
  import { computed } from "vue";
  import { useSlideTemplatesStore } from "@stores/slideTemplates";
  import { Card, CardContent } from "@/components/ui/card";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import SlideFieldEditor from "./SlideFieldEditor.vue";

  const skinTypeOptions = [
    { value: "cover",   label: "Cover" },
    { value: "section", label: "Section" },
    { value: "content", label: "Content" },
    { value: "end",     label: "End" },
  ];

  const props = defineProps({
    template: {
      type: Object,
      default: null,
    },
    sourceSlide: {
      type: Object,
      default: null,
    },
    allSlides: {
      type: Array,
      default: () => [],
    },
  });

  const store = useSlideTemplatesStore();

  const effectiveData = computed(() => {
    if (props.template) {
      return store.getEffectiveTemplateData(props.template.id) || props.template;
    }
    return { name: "", description: "", source_slide_index: null, editable_fields: [] };
  });

  const slideOptions = computed(() =>
    props.allSlides.map((s) => ({
      value: String(s.index),
      label: `Slide ${s.index + 1}`,
    })),
  );

  const allFields = computed(() => {
    return props.sourceSlide?.all_fields || [];
  });

  const sortedFields = computed(() => {
    const typeOrder = { text: 0, chart: 1, picture: 2, table: 3, shape: 4 };
    return [...allFields.value].sort((a, b) => {
      const orderA = typeOrder[a.type] ?? 5;
      const orderB = typeOrder[b.type] ?? 5;
      return orderA - orderB;
    });
  });

  function getFieldData(fieldName) {
    const fields = effectiveData.value.editable_fields || [];
    return fields.find((f) => f.name === fieldName);
  }

  function handleSourceSlideChange(value) {
    if (!props.template) return;
    const slideIndex = value != null ? Number(value) : null;
    store.updateLocalTemplate(props.template.id, "source_slide_index", slideIndex);

    // Auto-populate editable_fields with all fields from the selected slide (all editable by default).
    // The admin can then explicitly set individual fields to Non-editable or Background.
    if (slideIndex !== null) {
      const sourceSlide = props.allSlides.find((s) => s.index === slideIndex);
      if (sourceSlide) {
        const autoFields = (sourceSlide.all_fields || []).map((f) => ({
          name: f.name,
          type: f.type,
          label: "",
          description: "",
          editable: true,
        }));
        store.updateLocalTemplate(props.template.id, "editable_fields", autoFields);
      }
    }
  }

  function handleNameChange(value) {
    if (props.template) {
      store.updateLocalTemplate(props.template.id, "name", value);
    }
  }

  function handleSkinTypeChange(value) {
    if (props.template) {
      store.updateLocalTemplate(props.template.id, "skin_type", value || null);
    }
  }

  function handleDescriptionChange(value) {
    if (props.template) {
      store.updateLocalTemplate(props.template.id, "description", value);
    }
  }

  function updateFieldProperty(fieldName, property, value) {
    if (!props.template) return;
    const fields = JSON.parse(JSON.stringify(effectiveData.value.editable_fields || []));
    let field = fields.find((f) => f.name === fieldName);

    if (!field) {
      // Field not in editable_fields (background state) — re-add it as editable before
      // applying the property. Defensive only: background fields hide these inputs.
      const sourceField = allFields.value.find((f) => f.name === fieldName);
      if (!sourceField) return;
      field = { name: sourceField.name, type: sourceField.type, label: "", description: "", editable: true };
      fields.push(field);
    }

    field[property] = value;
    store.updateLocalTemplate(props.template.id, "editable_fields", fields);
  }

  function handleFieldStateChange(fieldName, state) {
    if (!props.template) return;
    const fields = JSON.parse(JSON.stringify(effectiveData.value.editable_fields || []));
    const fieldIndex = fields.findIndex((f) => f.name === fieldName);

    if (state === "background") {
      if (fieldIndex !== -1) fields.splice(fieldIndex, 1);
    } else {
      const editable = state === "editable";
      if (fieldIndex !== -1) {
        fields[fieldIndex].editable = editable;
      } else {
        const sourceField = allFields.value.find((f) => f.name === fieldName);
        if (sourceField) {
          fields.push({ name: sourceField.name, type: sourceField.type, label: sourceField.name, description: "", editable });
        }
      }
    }
    store.updateLocalTemplate(props.template.id, "editable_fields", fields);
  }

  function handleFieldLabelChange(fieldName, value) {
    updateFieldProperty(fieldName, "label", value);
  }

  function handleFieldDescriptionChange(fieldName, value) {
    updateFieldProperty(fieldName, "description", value);
  }

  function handleFieldStyleKeyChange(fieldName, value) {
    updateFieldProperty(fieldName, "report_theme_style_key", value || null);
  }
</script>

<template>
  <Card class="h-full flex flex-col overflow-hidden">
    <CardContent class="flex-1 overflow-y-auto p-6 space-y-6">
      <!-- Template name -->
      <div class="space-y-2">
        <Label>Name</Label>
        <Input
          :model-value="effectiveData.name"
          placeholder="Name..."
          class="max-w-xs"
          @update:model-value="handleNameChange" />
      </div>

      <!-- Source slide selector -->
      <div class="space-y-2">
        <Label>Source Slide</Label>
        <Select
          :model-value="effectiveData.source_slide_index != null ? String(effectiveData.source_slide_index) : null"
          :options="slideOptions"
          placeholder="Select a slide..."
          class="max-w-xs"
          @update:model-value="handleSourceSlideChange($event)" />
      </div>

      <!-- Template description -->
      <div class="space-y-2">
        <Label>Description (AI)</Label>
        <Textarea
          :model-value="effectiveData.description"
          placeholder="Description for AI..."
          :rows="3"
          class="resize-none"
          @update:model-value="handleDescriptionChange" />
      </div>

      <!-- Skin type -->
      <div class="space-y-2">
        <Label>Background</Label>
        <Select
          :model-value="effectiveData.skin_type || null"
          :options="skinTypeOptions"
          placeholder="Select a background..."
          clearable
          class="max-w-xs"
          @update:model-value="(v) => handleSkinTypeChange(v)" />
      </div>

      <!-- Editable fields section -->
      <div
        v-if="allFields.length > 0"
        class="space-y-3">
        <Label>Elements</Label>

        <!-- Field headers -->
        <div class="flex items-center gap-3 pb-1 border-b">
          <div class="w-24 flex-shrink-0">
            <span class="text-xs text-muted-foreground">Name</span>
          </div>
          <div class="w-16 flex-shrink-0">
            <span class="text-xs text-muted-foreground">Type</span>
          </div>
          <div class="w-32 flex-shrink-0">
            <span class="text-xs text-muted-foreground">Custom name</span>
          </div>
          <div class="flex-1">
            <span class="text-xs text-muted-foreground">Description (AI)</span>
          </div>
          <div class="w-28 flex-shrink-0">
            <span class="text-xs text-muted-foreground">Status</span>
          </div>
          <div class="w-44 flex-shrink-0">
            <span class="text-xs text-muted-foreground">Style</span>
          </div>
        </div>

        <!-- Field rows -->
        <SlideFieldEditor
          v-for="field in sortedFields"
          :key="field.name"
          :field="field"
          :field-data="getFieldData(field.name)"
          @state-change="handleFieldStateChange(field.name, $event)"
          @update:label="handleFieldLabelChange(field.name, $event)"
          @update:description="handleFieldDescriptionChange(field.name, $event)"
          @update:report-theme-style-key="handleFieldStyleKeyChange(field.name, $event)" />
      </div>

      <!-- No source slide selected -->
      <EmptyState
        v-else-if="effectiveData.source_slide_index == null"
        class="py-6"
        title="Select a source slide above to configure elements" />

      <!-- No fields available -->
      <EmptyState
        v-else
        class="py-6"
        title="No editable fields available for this slide" />
    </CardContent>
  </Card>
</template>
